// Updated: 2026-08-03 22:35:00 CDT
// Purpose: Apply user-approved character-name corrections without breaking screenplay order.

import type { Character } from "@/types/screenplay";

export const normalizeCharacterName = (value: string): string =>
  value.trim().replace(/\s+/g, " ");

export const renameScreenplayCharacter = (
  characters: Character[],
  currentName: string,
  requestedName: string,
): Character[] => {
  const nextName = normalizeCharacterName(requestedName);

  if (!nextName) {
    throw new Error("Enter a character name.");
  }

  if (nextName.length > 80) {
    throw new Error("Character names must be 80 characters or fewer.");
  }

  if (nextName === "SCREENPLAY_SEQUENCE") {
    throw new Error("SCREENPLAY_SEQUENCE is reserved for the ordered screenplay.");
  }

  if (currentName === "SCREENPLAY_SEQUENCE") {
    throw new Error("The ordered screenplay record cannot be renamed.");
  }

  if (nextName === currentName) {
    return characters;
  }

  const sourceCharacter = characters.find(
    (character) => character.name === currentName,
  );

  if (!sourceCharacter) {
    throw new Error(`Character ${currentName} was not found.`);
  }

  const existingTarget = characters.find(
    (character) => character.name === nextName,
  );

  return characters
    .filter(
      (character) =>
        character.name !== currentName || existingTarget === undefined,
    )
    .map((character) => {
      const sequence = character.sequence?.map((item) =>
        item.character === currentName
          ? { ...item, character: nextName }
          : item,
      );

      if (existingTarget && character.name === nextName) {
        return {
          ...character,
          lines: character.lines + sourceCharacter.lines,
          dialogue: [
            ...(character.dialogue ?? []),
            ...(sourceCharacter.dialogue ?? []),
          ],
          sequence,
        };
      }

      if (!existingTarget && character.name === currentName) {
        return {
          ...character,
          name: nextName,
          sequence,
        };
      }

      return sequence ? { ...character, sequence } : character;
    });
};

export const remapVoiceAssignment = (
  assignments: Record<string, string>,
  currentName: string,
  requestedName: string,
): Record<string, string> => {
  const nextName = normalizeCharacterName(requestedName);
  const currentVoiceId = assignments[currentName];
  const remappedAssignments = Object.fromEntries(
    Object.entries(assignments).filter(([character]) => character !== currentName),
  );

  if (currentVoiceId && !remappedAssignments[nextName]) {
    remappedAssignments[nextName] = currentVoiceId;
  }

  return remappedAssignments;
};
